import { useMemo, useState } from "react";
import { v4 as uuid } from "uuid";
import { Box, useMediaQuery } from "@mui/material";
import { useAppDispatch } from "../../../../../store/hooks";
import {
  removeAllBlocksOffs,
  removeSingleBlocksOff,
} from "../../../../../actions";
import { DangerButton } from "../../../../../components/button/button";
import { addNotification } from "../../../../../store/slices/ui-slice";
import { DeleteBlockOffModalStyled } from "./delete-blockoff-modal.styles";
import DialogComponent from "../../../../../components/dialog-component/dialog-component";
import { calculateBlockoffDays } from "../blockoffs.utils";
import { isSomething } from "../../../../../utils/common-utils";
import { useTheme } from "styled-components";

interface DeleteBlockOffModalPropsType {
  item: any;
  onClose: () => void;
  updateBlockoffs: any;
}
const DeleteBlockOffModal = ({
  item,
  onClose,
  updateBlockoffs,
}: DeleteBlockOffModalPropsType) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeletingAll, setIsDeletingAll] = useState(false);
  const [isDeleteAllModal, setIsDeleteAllModal] = useState(false);

  const handleConfirmDeleteAll = async () => {
    setIsDeletingAll(true);
    try {
      await removeAllBlocksOffs({
        id: item.id,
      });
      if (onClose) onClose();
      await updateBlockoffs();
    } catch (error: any) {
      dispatch(
        addNotification({
          id: uuid(),
          type: "error",
          message: error.response?.data?.message,
        })
      );
    } finally {
      setIsDeletingAll(false);
    }
  };

  const totalDaysSpan = useMemo(() => calculateBlockoffDays(item), [item]);

  const handleDeleteSingle = async () => {
    setIsDeleting(true);
    try {
      // Parse the date from item.label (format: "March 3, 2025")
      const dateParts = item.label.match(/(\w+)\s+(\d+),\s+(\d+)/);

      if (!dateParts) {
        throw new Error(`Invalid date format: ${item.label}`);
      }

      const [_, monthName, day, year] = dateParts;

      // Create a date object to get the month index (0-11)
      const tempDate = new Date(`${monthName} 1, 2000`);
      const monthIndex = tempDate.getMonth();

      if (isNaN(monthIndex)) {
        throw new Error(`Invalid month: ${monthName}`);
      }

      // Create the UTC date and format it as YYYY-MM-DD
      const date = new Date(
        Date.UTC(Number(year), monthIndex, Number(day), 0, 0, 0)
      );
      const formattedDate = date.toISOString().split("T")[0];

      const payload = {
        id: item.id,
        date: formattedDate,
      };

      console.log({ payload });

      await removeSingleBlocksOff(payload);
      if (onClose) onClose();
      await updateBlockoffs();
    } catch (error: any) {
      dispatch(
        addNotification({
          id: uuid(),
          type: "error",
          message:
            error.response?.data?.message || "Failed to delete appointment",
        })
      );
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <DialogComponent
      open
      title="Delete blockoff"
      content={
        <DeleteBlockOffModalStyled>
          {!isDeleteAllModal ? (
            <>
              <Box>
                Do you want to delete blockoff only of{" "}
                <strong>{item?.label}</strong> at{" "}
                <strong>
                  {item?.start_time} - {item?.end_time}
                </strong>{" "}
                {isSomething(item?.repeat_interval) && (
                  <>
                    {" "}
                    or want to delete all blockoffs of{" "}
                    <strong>{totalDaysSpan}</strong> days
                  </>
                )}
                ?
              </Box>
              <Box className="btn-wrapper">
                {isSomething(item?.repeat_interval) && (
                  <DangerButton
                    isLoading={false}
                    disabled={isDeleting}
                    variant="outlined"
                    onClick={() => setIsDeleteAllModal(true)}
                    className="delete-all-btn"
                  >
                    {isMobile ? "Delete all" : "Delete all blockoffs"}
                  </DangerButton>
                )}
                <DangerButton
                  isLoading={isDeleting}
                  disabled={isDeleting}
                  variant="contained"
                  onClick={handleDeleteSingle}
                  className="delete-only-btn"
                >
                  {isSomething(item?.repeat_interval)
                    ? isMobile
                      ? "Delete only"
                      : "Delete only this"
                    : "Delete"}
                </DangerButton>
              </Box>
            </>
          ) : (
            <>
              <Box>
                Are you sure you want to delete all blockoffs for the next{" "}
                <strong>{totalDaysSpan}</strong> days? This action cannot be
                undone.
              </Box>
              <Box className="btn-wrapper">
                <DangerButton
                  isLoading={isDeletingAll}
                  disabled={isDeletingAll}
                  variant="contained"
                  onClick={handleConfirmDeleteAll}
                >
                  Delete
                </DangerButton>
              </Box>
            </>
          )}
        </DeleteBlockOffModalStyled>
      }
      onClose={onClose}
    />
  );
};

export default DeleteBlockOffModal;
